/**
 * OrderHistoryContext - 주문 이력 상태 관리
 *
 * 주문 이력, 영수증 선택 상태 등을 제공합니다.
 * - 로그인 사용자: API에서 주문 목록 조회
 * - 비로그인 사용자: guestLookup API + localStorage fallback
 * 주문 완료 후 이력 저장 및 영수증 조회에 사용됩니다.
 */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useLayoutEffect,
  useRef,
  type ReactNode,
} from 'react';
import type { OrderHistoryItem, CartItem, LatestPaymentInfo } from '@/types';
import { generateUUID } from '@/lib/app-utils';
import { useAuth } from './AuthContext';
import { orderApi } from '@/lib/api/orders';
import { ApiClientError } from '@/lib/api/client';
import type { Order } from '@/lib/api/types';
import { logger } from '@/utils/logger';
import { deriveNetSubtotal } from '@/utils/pricing';
import { derivePaymentMethod } from '@/utils/receipt/derivePaymentMethod';
import { asGross, asNet, asTax, asTip, asTotal, makeReceiptAmounts } from '@/types/money';

// localStorage 키
const LOCAL_STORAGE_KEY = 'olulo_order_history';
const GUEST_CREDENTIALS_KEY = 'olulo_guest_credentials';

interface GuestCredentials {
  phone: string;
  password: string;
}

interface OrderCreationData {
  orderData: {
    orderId?: string;
    orderDbId?: number;
    timestamp?: Date;
    restaurant?: string;
    items?: CartItem[];
    /** GROSS 합계 (IVA 포함) — #299 에서 `subtotal` 에서 변경 */
    grossSubtotal?: number;
    /** NET 소계 (#284) — 결제 확정 시 FE가 계산해 전달, 또는 BE 응답에서 반영 */
    netSubtotal?: number;
    tax?: number;
    tip?: number;
    total?: number;
    paymentMethod?: string;
    /** #285 주문 시점 세율 비율 */
    taxRate?: number;
    /** #285 주문 시점 통화 */
    currency?: string;
    /** #647 C1: 결제 레코드 ID — 후불 '지금 결제' /charge 식별자 (createPaymentData.paymentId) */
    paymentId?: number;
    /** #647 C1: 결제 시점 축 스냅샷 (combination.timing) — 'postpay' 면 latestPayment 보존 */
    paymentTiming?: 'prepay' | 'postpay';
    /** #647 C1: 결제 조합 코드 스냅샷 (combination.code) */
    combinationCode?: string;
  } | null;
  cartItems: CartItem[];
  orderedItems: CartItem[];
  restaurantName: string;
  isTableOrder: boolean;
  tableNumber: string | null;
  /** 비회원 주문 시 전화번호 */
  customerPhone?: string;
  /** 비회원 주문 시 비밀번호 */
  guestPassword?: string;
}

interface OrderHistoryContextValue {
  // 상태
  orderHistory: OrderHistoryItem[];
  selectedOrderForReceipt: OrderHistoryItem | null;
  /** API 로딩 중 여부 */
  loading: boolean;
  /** API 에러 */
  error: Error | null;
  /** 비회원 인증 정보 존재 여부 */
  hasGuestCredentials: boolean;
  /** 비회원 인증 정보 — useActiveOrders 등 guestLookup 호출 훅에서 사용. 없으면 null. */
  guestCredentials: { phone: string; password: string } | null;
  // 액션
  addOrder: (order: OrderHistoryItem) => void;
  createOrderFromCheckout: (data: OrderCreationData) => OrderHistoryItem | null;
  updateOrderWithTaxInvoice: (orderId: string, taxInvoiceData: Record<string, unknown>) => void;
  selectOrderForReceipt: (order: OrderHistoryItem | null) => void;
  clearSelectedOrder: () => void;
  setSelectedOrderForReceipt: (order: OrderHistoryItem | null) => void;
  /** 주문 목록 새로고침 */
  refetch: () => Promise<void>;
  /** 비회원 인증 정보 저장 */
  saveGuestCredentials: (phone: string, password: string) => void;
  /** 비회원 인증 정보 삭제 */
  clearGuestCredentials: () => void;
}

const OrderHistoryContext = createContext<OrderHistoryContextValue | null>(null);

interface OrderHistoryProviderProps {
  children: ReactNode;
}

/**
 * API Order를 OrderHistoryItem으로 변환
 *
 * @internal 단위 테스트(`contexts/__tests__/orderHistoryMapper.test.ts`)에서 직접 검증.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function convertApiOrderToHistoryItem(order: Order): OrderHistoryItem {
  // #336 (#301 Phase 3 후속): wire (raw number) → makeReceiptAmounts 통과로
  // 불변식 (GROSS = NET + TAX) 캡슐화. Order DTO 의 raw gross_subtotal 직접
  // 부여 대신 net + tax 로부터 재산출 — drift 차단.
  const amounts = makeReceiptAmounts({
    net: asNet(order.net_subtotal),
    tax: asTax(order.tax),
    tip: asTip(order.tip ?? 0),
  });

  return {
    id: `order-${order.id}`,
    orderNumber: order.order_number || String(order.id),
    date: new Date(order.created_at),
    restaurant: order.store_name,
    restaurantId: String(order.store_id), // API store_id → restaurantId 매핑
    type: order.order_type,
    tableNumber: order.table_number ?? undefined,
    // items[].price 는 "단가" 계약 — 영수증 렌더러(MenuItemsList, OrderItemsList,
    // ReceiptModal 텍스트 다운로드)가 price * quantity 로 라인 합계를 재계산한다.
    // 따라서 API의 unit_price 를 사용해야 하며, total_price 로 저장하면 수량이
    // 중복 곱해져 금액이 2배 이상 부풀려진다.
    // BE 응답에서 items 누락 가능성 방어 (#447 hot-fix 광역 적용 / #449 Zod 도입 전 임시)
    items: (order.items ?? []).map((item) => ({
      name: item.menu_name,
      quantity: item.quantity,
      price: item.unit_price,
    })),
    grossSubtotal: amounts.grossSubtotal,
    netSubtotal: amounts.netSubtotal,
    tax: amounts.tax,
    tip: amounts.tip,
    total: amounts.total,
    // #285 stale 방지: 주문 시점 세율(퍼센트 → 비율)·통화 스냅샷 저장
    taxRate: order.tax_rate != null ? order.tax_rate / 100 : undefined,
    currency: order.currency,
    // #632 F-19: 'Card' 하드코딩 제거 — 결제 확정 시 combination_code 도출, 미확정은 ''(행 숨김).
    paymentMethod: derivePaymentMethod(order.latest_payment),
    // 주문 **단위** 결제 상태 (#854). latest_payment 는 분할 시 주문을 대표하지 못한다.
    paymentState: order.payment_state ?? null,
    paymentOutstandingAmount: order.payment_outstanding_amount ?? null,
    // C2 (#590): latest_payment snake_case → camelCase 변환
    // C3 (#590): id 추가 — 후불 "지금 결제" /charge 경로 paymentId. admin 미노출 시 undefined.
    latestPayment: order.latest_payment
      ? ({
          id: order.latest_payment.id,
          status: order.latest_payment.status,
          isPaid: order.latest_payment.is_paid,
          timing: order.latest_payment.timing,
          combinationCode: order.latest_payment.combination_code,
          paidAt: order.latest_payment.paid_at,
        } satisfies LatestPaymentInfo)
      : null,
  };
}

/**
 * guestLookup summary 는 BE 보안 정책상 order_type/items 없이 내려온다.
 * API validation layer 가 이를 pickup/[] 로 정규화하므로, 같은 브라우저에서 생성한
 * 로컬 snapshot 이 있으면 표시/영수증에 필요한 라인·테이블·금액 필드를 보강한다.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function mergeApiOrderWithLocalSnapshot(
  apiOrder: OrderHistoryItem,
  localOrder: OrderHistoryItem | undefined,
): OrderHistoryItem {
  if (!localOrder || apiOrder.items.length > 0 || localOrder.items.length === 0) {
    return apiOrder;
  }

  return {
    ...apiOrder,
    restaurantId:
      apiOrder.restaurantId && apiOrder.restaurantId !== '0'
        ? apiOrder.restaurantId
        : localOrder.restaurantId,
    type: localOrder.type,
    tableNumber: apiOrder.tableNumber ?? localOrder.tableNumber,
    items: localOrder.items,
    grossSubtotal: localOrder.grossSubtotal,
    netSubtotal: localOrder.netSubtotal,
    tax: localOrder.tax,
    tip: localOrder.tip,
    // codex C2/I2: 서버 total 은 권위값 — 유효한 0 을 로컬 값으로 오염시키지 않도록 ?? 사용.
    total: apiOrder.total ?? localOrder.total,
    taxRate: apiOrder.taxRate ?? localOrder.taxRate,
    currency: apiOrder.currency ?? localOrder.currency,
    // codex C2: paymentMethod 는 서버 도출값(derivePaymentMethod, 미결제='') 유지.
    // 로컬 snapshot 의 체크아웃 선택 수단으로 덮으면 미결제 후불에 선표기가 재발한다(#632 F-19).
    completedTaxInvoiceData: localOrder.completedTaxInvoiceData,
    taxInvoiceStatus: localOrder.taxInvoiceStatus,
  };
}

/**
 * localStorage에서 주문 목록 로드
 */
function loadOrdersFromLocalStorage(): OrderHistoryItem[] {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!stored) return [];

    const parsed = JSON.parse(stored) as Array<{
      id: string;
      orderNumber: string;
      date: string;
      restaurant: string;
      restaurantId?: string; // 선택적 필드 (하위 호환성)
      type: 'pickup' | 'table';
      tableNumber?: string;
      items: Array<{ name: string; quantity: number; price: number }>;
      /** #299 이전 localStorage 데이터 호환용 레거시 필드 (의미: GROSS) */
      subtotal?: number;
      grossSubtotal?: number;
      netSubtotal?: number;
      tax: number;
      tip: number;
      total: number;
      paymentMethod: string;
      taxInvoiceStatus?: 'available' | 'unavailable' | 'completed';
      completedTaxInvoiceData?: Record<string, unknown>;
      /** #647 C1: 후불 로컬 snapshot 의 미결제 상태 보존 (round-trip) */
      latestPayment?: LatestPaymentInfo | null;
    }>;

    // date 문자열을 Date 객체로 변환 + restaurantId/필드명 마이그레이션.
    // #299: 레거시 localStorage 의 `subtotal` 은 GROSS 로 간주한다(#284 이전
    // convertApiOrderToHistoryItem 이 `order.subtotal`(GROSS)을 그대로 저장했음).
    // `netSubtotal` 누락 시 저장된 `tax` 로 GROSS - tax 역산 — 그냥 grossSubtotal
    // 로 폴백하면 영수증 Subtotal 행에 IVA 포함 금액이 출력되어 "NET + IVA = Total"
    // 불변식이 깨진다.
    return parsed.map((order) => {
      const grossSubtotal = order.grossSubtotal ?? order.subtotal ?? 0;
      const netSubtotal = order.netSubtotal ?? deriveNetSubtotal(grossSubtotal, order.tax);
      if (order.netSubtotal === undefined) {
        logger.warn('[OrderHistory] 레거시 주문 netSubtotal 누락 — GROSS-tax 역산', {
          orderId: order.id,
          grossSubtotal,
          tax: order.tax,
          derivedNetSubtotal: netSubtotal,
        });
      }
      return {
        ...order,
        date: new Date(order.date),
        restaurantId: order.restaurantId || '', // 하위 호환성: 없으면 빈 문자열
        grossSubtotal: asGross(grossSubtotal),
        netSubtotal: asNet(netSubtotal),
        tax: asTax(order.tax),
        tip: asTip(order.tip),
        total: asTotal(order.total),
      };
    });
  } catch (error) {
    logger.error('[OrderHistory] Failed to load from localStorage:', error);
    return [];
  }
}

/**
 * localStorage에 주문 목록 저장
 */
function saveOrdersToLocalStorage(orders: OrderHistoryItem[]): void {
  try {
    // 최근 50개만 저장 (용량 제한)
    const ordersToSave = orders.slice(0, 50);
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(ordersToSave));
  } catch (error) {
    logger.error('[OrderHistory] Failed to save to localStorage:', error);
  }
}

/**
 * localStorage에서 비회원 인증 정보 로드
 */
function loadGuestCredentials(): GuestCredentials | null {
  try {
    const stored = localStorage.getItem(GUEST_CREDENTIALS_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as GuestCredentials;
  } catch (error) {
    logger.error('[OrderHistory] Failed to load guest credentials:', error);
    return null;
  }
}

/**
 * localStorage에 비회원 인증 정보 저장
 */
function saveGuestCredentialsToStorage(credentials: GuestCredentials): void {
  try {
    localStorage.setItem(GUEST_CREDENTIALS_KEY, JSON.stringify(credentials));
  } catch (error) {
    logger.error('[OrderHistory] Failed to save guest credentials:', error);
  }
}

/**
 * localStorage에서 비회원 인증 정보 삭제
 */
function clearGuestCredentialsFromStorage(): void {
  try {
    localStorage.removeItem(GUEST_CREDENTIALS_KEY);
  } catch (error) {
    logger.error('[OrderHistory] Failed to clear guest credentials:', error);
  }
}

export function OrderHistoryProvider({ children }: OrderHistoryProviderProps) {
  const { isLoggedIn, user, customerId } = useAuth();

  // 로컬 주문 내역 (비로그인 사용자 + 로그인 사용자의 새 주문)
  const [localOrders, setLocalOrders] = useState<OrderHistoryItem[]>(() =>
    loadOrdersFromLocalStorage()
  );

  // API 주문 내역 (로그인/비로그인 사용자)
  const [apiOrders, setApiOrders] = useState<OrderHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // 비회원 인증 정보
  const [guestCredentials, setGuestCredentials] = useState<GuestCredentials | null>(() =>
    loadGuestCredentials()
  );

  const [selectedOrderForReceipt, setSelectedOrderForReceipt] = useState<OrderHistoryItem | null>(
    null
  );

  // API results belong to one committed identity and one request. Guest credentials
  // remain in memory; never serialize them into request identifiers or diagnostics.
  const accountId = isLoggedIn ? (user?.id ?? customerId) : null;
  const owner = useMemo(
    () => ({ isLoggedIn, accountId, guest: isLoggedIn ? null : guestCredentials }),
    [isLoggedIn, accountId, guestCredentials]
  );
  const activeOwner = useRef<typeof owner | null>(null);
  const requestSequence = useRef(0);
  const [apiOrdersOwner, setApiOrdersOwner] = useState<typeof owner | null>(null);

  useLayoutEffect(() => {
    activeOwner.current = owner;
    requestSequence.current += 1;
    setApiOrders([]);
    setApiOrdersOwner(null);
    setLoading(false);
    setError(null);
    return () => {
      activeOwner.current = null;
      requestSequence.current += 1;
    };
  }, [owner]);

  const beginRequest = useCallback(() => {
    if (activeOwner.current !== owner) return null;
    const sequence = ++requestSequence.current;
    return () => activeOwner.current === owner && requestSequence.current === sequence;
  }, [owner]);

  /**
   * API에서 로그인 사용자 주문 목록 조회
   */
  const fetchOrdersFromApi = useCallback(async () => {
    const isCurrent = beginRequest();
    if (!isCurrent) return;
    setLoading(true);
    setError(null);

    try {
      const response = await orderApi.getOrders({ per_page: 50 });
      if (!isCurrent()) return;
      const convertedOrders = response.data.map(convertApiOrderToHistoryItem);
      setApiOrders(convertedOrders);
      setApiOrdersOwner(owner);
    } catch (err) {
      if (!isCurrent()) return;
      logger.error('[OrderHistory] Failed to fetch orders from API:', err);
      setError(err instanceof Error ? err : new Error('주문 목록을 불러오는데 실패했습니다'));
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [beginRequest, owner]);

  /**
   * 비회원 주문 조회 (guestLookup API)
   */
  const fetchGuestOrders = useCallback(async (credentials: GuestCredentials) => {
    const isCurrent = beginRequest();
    if (!isCurrent) return;
    // #641 후속 P1 (fail-closed degrade): 테이블 주문 전화번호 선택화 이후
    // 빈 phone 자격증명이 localStorage 에 남아있을 수 있음 (기존 사용자 포함).
    // 빈/공백 phone 으로 guestLookup 을 호출하면 admin 의 phone required 검증에
    // 걸려 매번 422 + Sentry capture → API 호출 자체를 skip 하고 로컬 snapshot 만
    // 사용 (에러 토스트/Sentry 없는 정상 degrade).
    if (!credentials.phone || credentials.phone.trim() === '') {
      setApiOrders([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await orderApi.guestLookup({
        phone: credentials.phone,
        password: credentials.password,
      });
      if (!isCurrent()) return;
      const convertedOrders = response.data.map(convertApiOrderToHistoryItem);
      setApiOrders(convertedOrders);
      setApiOrdersOwner(owner);
    } catch (err) {
      if (!isCurrent()) return;
      logger.error('[OrderHistory] Failed to fetch guest orders:', err);

      // 인증 실패 시 (401/403) 저장된 credentials 삭제
      const isAuthError =
        err instanceof ApiClientError && (err.status === 401 || err.status === 403);

      if (isAuthError) {
        // The failed lookup above owns the diagnostic; clearing is its handled recovery.
        logger.info('[OrderHistory] Guest credentials invalid, clearing...');
        setGuestCredentials(null);
        clearGuestCredentialsFromStorage();
      }

      // 비회원 조회 실패 시 에러는 설정하지만, 로컬 주문은 여전히 표시
      setError(err instanceof Error ? err : new Error('주문 조회에 실패했습니다'));
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [beginRequest, owner]);

  // 로그인 상태 변경 시 API에서 주문 목록 조회
  useEffect(() => {
    if (isLoggedIn) {
      fetchOrdersFromApi();
    } else if (guestCredentials) {
      // 비로그인 + 비회원 인증 정보 있으면 guestLookup 호출
      fetchGuestOrders(guestCredentials);
    } else {
      // 로그아웃 시 API 주문 목록 초기화
      setApiOrders([]);
    }
  }, [isLoggedIn, guestCredentials, fetchOrdersFromApi, fetchGuestOrders]);

  // 로컬 주문이 변경될 때마다 localStorage에 저장
  useEffect(() => {
    saveOrdersToLocalStorage(localOrders);
  }, [localOrders]);

  /**
   * 주문 목록 새로고침
   */
  const refetch = useCallback(async () => {
    if (isLoggedIn) {
      await fetchOrdersFromApi();
    } else if (guestCredentials) {
      await fetchGuestOrders(guestCredentials);
    }
  }, [isLoggedIn, guestCredentials, fetchOrdersFromApi, fetchGuestOrders]);

  /**
   * 비회원 인증 정보 저장
   *
   * #647 P2-1 (경계 방어): 호출부가 아닌 저장 경계에서 정규화/거부.
   * - phone 은 trim 정규화 — 공백만 입력되면 '' 로 저장 (전화 없는 테이블 게스트).
   *   빈 phone 도 password 는 보존해야 세션 미결제 조회/합산청구(password 헤더·
   *   guest_password) 경로가 동작한다 (#647 C2).
   * - password 빈 값(공백 포함)은 저장 거부 — 자격증명으로서 무의미하며 기존
   *   유효 자격증명을 덮어쓰는 사고를 차단.
   */
  const saveGuestCredentials = useCallback((phone: string, password: string) => {
    if (!password || password.trim() === '') {
      logger.warn('[OrderHistory] 빈 password 자격증명 저장 거부 — 기존 자격증명 유지');
      return;
    }
    const credentials = { phone: phone.trim(), password };
    requestSequence.current += 1;
    setGuestCredentials(credentials);
    saveGuestCredentialsToStorage(credentials);
  }, []);

  /**
   * 비회원 인증 정보 삭제
   */
  const clearGuestCredentials = useCallback(() => {
    requestSequence.current += 1;
    setGuestCredentials(null);
    clearGuestCredentialsFromStorage();
    setApiOrders([]);
  }, []);

  /**
   * 통합된 주문 목록 (API + 로컬)
   * - 로그인 사용자: API 주문 + 로컬 주문 (중복 제거)
   * - 비로그인 사용자 (인증 정보 있음): API 주문 + 로컬 주문 (중복 제거)
   * - 비로그인 사용자 (인증 정보 없음): 로컬 주문만
   */
  const orderHistory = useMemo(() => {
    if (isLoggedIn || guestCredentials) {
      const currentApiOrders = apiOrdersOwner === owner ? apiOrders : [];
      const localOrdersById = new Map(localOrders.map((order) => [order.id, order]));
      const mergedApiOrders = currentApiOrders.map((order) =>
        mergeApiOrderWithLocalSnapshot(order, localOrdersById.get(order.id))
      );
      const apiOrderIds = new Set(currentApiOrders.map((o) => o.id));
      // 로컬 주문 중 API에 없는 것만 추가 (방금 생성한 주문)
      const uniqueLocalOrders = localOrders.filter((o) => !apiOrderIds.has(o.id));
      // API 주문 + 로컬 고유 주문, 날짜순 정렬
      return [...mergedApiOrders, ...uniqueLocalOrders].sort(
        (a, b) => b.date.getTime() - a.date.getTime()
      );
    }
    // 비로그인 + 인증 정보 없음: 로컬 주문만
    return localOrders.sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [isLoggedIn, guestCredentials, apiOrders, apiOrdersOwner, owner, localOrders]);

  // 주문 추가 (로컬에 저장)
  const addOrder = useCallback((order: OrderHistoryItem) => {
    setLocalOrders((prev) => [order, ...prev]);
  }, []);

  // 체크아웃에서 주문 생성
  const createOrderFromCheckout = useCallback(
    (data: OrderCreationData): OrderHistoryItem | null => {
      const {
        orderData,
        cartItems,
        orderedItems,
        restaurantName,
        isTableOrder,
        tableNumber,
        customerPhone,
        guestPassword,
      } = data;

      const itemsForSnapshot =
        orderData?.items && orderData.items.length > 0
          ? orderData.items
          : [...orderedItems, ...cartItems];

      // orderData가 없거나 주문할 아이템이 전혀 없는 경우
      if (!orderData || itemsForSnapshot.length === 0) {
        return null;
      }

      // 비회원 주문인 경우 인증 정보 저장
      if (customerPhone && guestPassword) {
        saveGuestCredentials(customerPhone, guestPassword);
      }

      // crypto.randomUUID() 또는 fallback으로 generateUUID() 사용
      const uuid =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : generateUUID();

      // 주문번호도 더 안전한 방식으로 생성
      const orderNumber =
        typeof crypto !== 'undefined' && crypto.getRandomValues
          ? (Array.from(crypto.getRandomValues(new Uint32Array(1)))[0] % 900000) + 100000
          : Math.floor(100000 + Math.random() * 900000);

      const id =
        typeof orderData.orderDbId === 'number'
          ? `order-${orderData.orderDbId}`
          : `order-${uuid}`;
      const orderNumberForDisplay = orderData.orderId || String(orderNumber);

      const newOrder: OrderHistoryItem = {
        id,
        orderNumber: orderNumberForDisplay,
        date: orderData.timestamp ?? new Date(),
        restaurant: restaurantName || orderData.restaurant || 'Restaurant',
        restaurantId: itemsForSnapshot[0]?.restaurantId || '',
        type: isTableOrder ? 'table' : 'pickup',
        tableNumber: tableNumber || undefined,
        items: itemsForSnapshot.map((item) => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price,
        })),
        grossSubtotal: asGross(orderData.grossSubtotal || 0),
        // #284: 로컬 주문도 netSubtotal 을 보존해야 결제 직후 영수증 재조회 시
        // grossSubtotal 로 되돌아가지 않는다. #299 이후 checkout flow 가 항상
        // netSubtotal 을 채워 넘기지만(createPaymentData), 상위 타입이 optional
        // 이라 런타임 가드는 유지 — 누락 시 GROSS-tax 역산 + 로깅.
        netSubtotal: asNet(
          orderData.netSubtotal ??
            (() => {
              logger.warn('[OrderHistory] checkout flow netSubtotal 누락 — GROSS-tax 역산', {
                grossSubtotal: orderData.grossSubtotal,
                tax: orderData.tax,
              });
              return deriveNetSubtotal(orderData.grossSubtotal ?? 0, orderData.tax);
            })()
        ),
        tax: asTax(orderData.tax || 0),
        tip: asTip(orderData.tip || 0),
        total: asTotal(orderData.total || 0),
        // #285 stale 방지: 주문 시점 세율·통화 스냅샷 (체크아웃에서 orderData 로 받아옴)
        taxRate: orderData.taxRate,
        currency: orderData.currency,
        // #632 F-19: 'Card' fallback 하드코딩 제거 — 미상이면 ''(표시 계층이 행 숨김).
        paymentMethod: orderData.paymentMethod || '',
        // #647 C1: 후불 주문은 로컬 snapshot 에도 미결제 상태(latestPayment)를 보존.
        // 빈 phone 게스트(guestLookup skip)는 로컬 주문만 보이므로, 이 정보가 없으면
        // OrderHistoryPage 의 미결제 후불 구분/세션 결제 진입 게이트가 사라진다.
        // 주문 직후 후불 결제는 항상 pending/미결제 — prepay 는 기존대로 null.
        latestPayment:
          orderData.paymentTiming === 'postpay'
            ? ({
                id: orderData.paymentId,
                status: 'pending',
                isPaid: false,
                timing: 'postpay',
                combinationCode: orderData.combinationCode ?? null,
                paidAt: null,
              } satisfies LatestPaymentInfo)
            : null,
      };

      setLocalOrders((prev) => [newOrder, ...prev.filter((order) => order.id !== newOrder.id)]);
      return newOrder;
    },
    [saveGuestCredentials]
  );

  // 주문에 세금계산서 데이터 추가
  const updateOrderWithTaxInvoice = useCallback(
    (orderId: string, taxInvoiceData: Record<string, unknown>) => {
      setLocalOrders((prev) =>
        prev.map((order) =>
          order.id === orderId ? { ...order, completedTaxInvoiceData: taxInvoiceData } : order
        )
      );
    },
    []
  );

  // 영수증용 주문 선택
  const selectOrderForReceipt = useCallback((order: OrderHistoryItem | null) => {
    setSelectedOrderForReceipt(order);
  }, []);

  // 영수증 선택 초기화
  const clearSelectedOrder = useCallback(() => {
    setSelectedOrderForReceipt(null);
  }, []);

  const value = useMemo<OrderHistoryContextValue>(
    () => ({
      orderHistory,
      selectedOrderForReceipt,
      loading,
      error,
      hasGuestCredentials: guestCredentials !== null,
      guestCredentials,
      addOrder,
      createOrderFromCheckout,
      updateOrderWithTaxInvoice,
      selectOrderForReceipt,
      clearSelectedOrder,
      setSelectedOrderForReceipt,
      refetch,
      saveGuestCredentials,
      clearGuestCredentials,
    }),
    [
      orderHistory,
      selectedOrderForReceipt,
      loading,
      error,
      guestCredentials,
      addOrder,
      createOrderFromCheckout,
      updateOrderWithTaxInvoice,
      selectOrderForReceipt,
      clearSelectedOrder,
      // setSelectedOrderForReceipt는 useState setter로 안정적인 참조이므로 의존성에서 제외
      refetch,
      saveGuestCredentials,
      clearGuestCredentials,
    ]
  );

  return <OrderHistoryContext.Provider value={value}>{children}</OrderHistoryContext.Provider>;
}

/**
 * useOrderHistory - OrderHistoryContext 사용 훅
 */
export function useOrderHistory(): OrderHistoryContextValue {
  const context = useContext(OrderHistoryContext);
  if (!context) {
    throw new Error('useOrderHistory must be used within an OrderHistoryProvider');
  }
  return context;
}
